import { ArrowUpDown, Check, X } from 'lucide-react';
import { useCanArrangeTechnicians } from '../../../shared/hooks/useUserRoles';
import type { CalendarOrderEditor } from '../model/useCalendarOrderEditor';

interface Props {
  editor: CalendarOrderEditor;
  /** Planen and Umplanen have their own order; the editor is off while one is going on. */
  disabled?: boolean;
}

/**
 * The board's own button language (see the Erneut-versuchen button and the planning bar): 1px slate
 * outline, the brand blue #124093 for the one action that matters, a gray-50 hover. 32px square
 * keeps three of them inside the 200px the corner cell leaves beside "Handwerkern".
 */
const BUTTON =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#124093] focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50';
const OUTLINED = 'border-slate-200 bg-white text-[#124093] hover:bg-gray-50';
const FILLED = 'border-[#124093] bg-[#124093] text-white hover:bg-[#0e3277]';
const FAILED = 'border-red-600 bg-red-600 text-white hover:bg-red-700';

/**
 * The edit-mode controls for the Leitung, sized to sit beside "Handwerkern" in the board's corner
 * cell (224px wide): icon buttons with a name and a tooltip rather than text. Renders nothing for
 * anyone else — they see the order, they do not change it.
 */
export const CalendarOrderToolbar = ({ editor, disabled = false }: Props) => {
  const canArrange = useCanArrangeTechnicians();
  if (!canArrange) return null;

  if (!editor.isEditing) {
    const label = disabled ? 'Reihenfolge bearbeiten (nicht während des Planens)' : 'Reihenfolge bearbeiten';

    return (
      <button
        type="button"
        onClick={editor.start}
        disabled={disabled}
        aria-label="Reihenfolge bearbeiten"
        title={label}
        className={`${BUTTON} ${OUTLINED}`}
      >
        <ArrowUpDown size={16} aria-hidden />
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={editor.requestCancel}
        disabled={editor.isSaving}
        aria-label="Abbrechen"
        title="Abbrechen"
        className={`${BUTTON} border-slate-200 bg-white text-slate-600 hover:bg-gray-50`}
      >
        <X size={16} aria-hidden />
      </button>
      <button
        type="button"
        onClick={() => void editor.save()}
        disabled={editor.isSaving}
        aria-label="Speichern"
        title={editor.isError ? 'Speichern fehlgeschlagen — erneut versuchen' : 'Speichern'}
        className={`${BUTTON} ${editor.isError ? FAILED : FILLED} ${
          editor.isSaving ? 'animate-pulse' : ''
        }`}
      >
        <Check size={16} aria-hidden />
      </button>
    </div>
  );
};
